import { LogicBlock } from './logic-block.js'
import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
	jsonMapProperty,
} from '@sumbricht/json-class-serializer'
import { Parameter } from '../../script/parameter.js'
import { EditorPropertyWithKey } from '../decorators/editor-property-types.js'
import { Screen } from '../../bos/screen.js'
import { assignCommonProperties } from '../../util/data/assign-common-properties.js'

@jsonClass('ScopeBlock')
export class ScopeBlock extends LogicBlock {
	@jsonArrayProperty(Parameter) variables: Parameter[] = []
	@jsonArrayProperty(Parameter) declarationOnlyVariables: Parameter[] = []

	constructor(init: Partial<ScopeBlock> = {}) {
		super()
		assignCommonProperties(this, init)
	}

	static createNewForEditor() {
		return new this({
			variables: [new Parameter({ name: 'variable1', type: 'string' })],
		})
	}

	public getOwnVariables(
		includeDeclarationVariables: boolean,
	): Record<string, string | null> {
		let vars = Object.fromEntries(this.variables.map((v) => [v.name, v.type]))
		if (includeDeclarationVariables) {
			vars = {
				...vars,
				...Object.fromEntries(
					this.declarationOnlyVariables.map((v) => [v.name, v.type]),
				),
			}
		}

		return vars
	}

	getEditorCategory(): string {
		return 'Logic'
	}

	getAdditionalEditorProperties() {
		const properties =
			this.variables?.flatMap(
				(v, idx) =>
					[
						{
							inputType: 'input',
							elementType: 'input',
							contentType: 'text',
							order: 3 * idx,
							label: `Variable name ${idx + 1}`,
							key: `variables.${idx}.name`,
							getter: (item, key) => item.variables[idx].name,
							setter: (item, key, value) => (item.variables[idx].name = value),
						},
						{
							inputType: 'code',
							order: 3 * idx + 1,
							label: `Variable type ${idx + 1}`,
							key: `variables.${idx}.type`,
							codeLanguage: 'ts',
							tsReturnType: () => 'any',
							editorSize: 'singleline',
							isBinding: false,
							getter: (item, key) => item.variables[idx].type,
							setter: (item, key, value) => (item.variables[idx].type = value),
						},
						{
							inputType: 'code',
							order: 3 * idx + 2,
							label: `Variable value ${idx + 1}`,
							key: `variables.${idx}.defaultExpression`,
							codeLanguage: 'ts',
							tsReturnType: (items) => items[0].variables[idx].type,
							editorSize: 'singleline',
							isBinding: false,
							getter: (item, key) => item.variables[idx].defaultExpression,
							setter: (item, key, value) =>
								(item.variables[idx].defaultExpression = value),
							quickActions: [
								{
									name: 'Delete',
									action: (items) => items[0].variables.splice(idx, 1),
									isVisible: (items) => items[0].variables.length > 1,
								},
								{
									name: 'Add variable',
									shortcutKey: '+',
									action: (items) =>
										items[0].variables.splice(
											idx + 1,
											0,
											new Parameter({ name: 'variable', type: 'string' }),
										),
								},
							],
						},
					] as EditorPropertyWithKey<Screen, ScopeBlock>[],
			) ?? []

		return properties
	}
}
